/**
 * Qualification Checkbox (Epic C-2 — Kwalificaties, Story C-2.2)
 *
 * Zelfstandige toggle-cel, hergebruikt door zowel `PerMedewerkerMatrix`
 * (rijen=productcategorieën × kolommen=doelmarkten) als `WieKanWatMatrix`
 * (rij-per-combinatie) — "zelfde mutatie, andere ingang" (UX-C3).
 *
 * Optimistisch bijgewerkt met lokale override-state + rollback-op-fout
 * (rode toast, AC 6 test-guidance) — geen React Query `onMutate`-cache-
 * patching, want twee structureel verschillende query-vormen (matrix-per-
 * medewerker vs. wie-kan-wat) zouden dat onnodig complex maken; na een
 * geslaagde mutatie blijft de lokale override de zichtbare waarheid
 * (die inmiddels met de server overeenkomt) terwijl de achtergrond-
 * invalidatie (in de hooks) de caches alsnog ververst.
 *
 * Uitzetten (AD-15): haalt eerst de `dryRun`-telling op; > 0 toekomstige
 * indelingen → confirm-dialog (UX-C3); 0 → direct uitzetten, geen dialog.
 */
import { useState } from 'react';
import { targetMarketKey } from '@functieloonhuis/shared';
import { useToast } from '../../../components/Toast';
import { qualificationsApi } from '../../../lib/qualificationsApi';
import { useGrantQualification, useRevokeQualification } from '../hooks/useQualifications';
import { QualificationRevokeConfirmDialog } from './QualificationRevokeConfirmDialog';
import type { PlanningTaskActivity } from '@functieloonhuis/shared';

interface QualificationCheckboxProps {
  employeeId: number;
  employeeName: string;
  activity: PlanningTaskActivity;
  productCategoryId: number;
  /** `null` bij een activiteit zonder doelmarkt — zie `activityUsesTargetMarket`. */
  targetMarketId: number | null;
  qualified: boolean;
  ariaLabel: string;
}

export function QualificationCheckbox({
  employeeId,
  employeeName,
  activity,
  productCategoryId,
  targetMarketId,
  qualified,
  ariaLabel,
}: QualificationCheckboxProps) {
  const grant = useGrantQualification();
  const revoke = useRevokeQualification();
  const { showToast } = useToast();

  const [override, setOverride] = useState<boolean | null>(null);
  const [confirmCount, setConfirmCount] = useState<number | null>(null);
  const [isCheckingDryRun, setIsCheckingDryRun] = useState(false);

  const checked = override ?? qualified;
  // Via `targetMarketKey`, zodat een markt-loze cel `…-0` heet en niet
  // `…-null`: één selector-conventie voor alle zes de activiteiten.
  const testId = `kwalificatie-checkbox-${employeeId}-${activity}-${productCategoryId}-${targetMarketKey(targetMarketId)}`;
  const input = { employeeId, activity, productCategoryId, targetMarketId };

  async function doGrant() {
    setOverride(true);
    try {
      await grant.mutateAsync(input);
    } catch {
      setOverride(false);
      showToast('Opslaan mislukt — probeer opnieuw.', 'error');
    }
  }

  async function doRevoke() {
    setOverride(false);
    try {
      await revoke.mutateAsync(input);
    } catch {
      setOverride(true);
      showToast('Opslaan mislukt — probeer opnieuw.', 'error');
    }
  }

  async function handleToggle() {
    if (!checked) {
      await doGrant();
      return;
    }

    setIsCheckingDryRun(true);
    try {
      const { upcomingAssignmentCount } = await qualificationsApi.dryRunRevoke(input);
      if (upcomingAssignmentCount > 0) {
        setConfirmCount(upcomingAssignmentCount);
        // Bewust NIET vrijgeven: zolang de bevestigingsdialoog openstaat, moet
        // het vakje geblokkeerd blijven. Gaf `finally` het hier vrij, dan
        // startte een tweede klik een tweede telling, en klapte er ná het
        // intrekken opnieuw een dialoog open voor een kwalificatie die al weg
        // is — bevestigen daar stuurde dan een tweede revoke.
        return;
      }
      await doRevoke();
      setIsCheckingDryRun(false);
    } catch {
      showToast('Ophalen van de telling is mislukt — probeer opnieuw.', 'error');
      setIsCheckingDryRun(false);
    }
  }

  return (
    <>
      <input
        type="checkbox"
        data-testid={testId}
        aria-label={ariaLabel}
        checked={checked}
        disabled={isCheckingDryRun || grant.isPending || revoke.isPending}
        onChange={handleToggle}
        className="h-4 w-4 rounded border-[#E2E8F0] text-[#54949E] focus:ring-[#54949E] disabled:cursor-not-allowed disabled:opacity-50"
      />
      {confirmCount !== null && (
        <QualificationRevokeConfirmDialog
          employeeFirstName={employeeName.split(' ')[0] ?? employeeName}
          upcomingAssignmentCount={confirmCount}
          onCancel={() => {
            setConfirmCount(null);
            setIsCheckingDryRun(false);
          }}
          onConfirm={async () => {
            setConfirmCount(null);
            await doRevoke();
            setIsCheckingDryRun(false);
          }}
          isPending={revoke.isPending}
        />
      )}
    </>
  );
}
